import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback stock images if no images in folders
const stockImages = [
  { 
    url: 'https://images.unsplash.com/photo-1767081697021-66be307d95f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxwYXJlbnRzJTIwcGxheWluZyUyMHdpdGglMjBjaGlsZHJlbiUyMHBhcmslMjBoYXBweSUyMGxpZmVzdHlsZXxlbnwwfHx8fDE3NzE2OTE0ODJ8MA&ixlib=rb-4.1.0&q=85',
    category: 'family',
  },
  { 
    url: 'https://images.unsplash.com/photo-1761891931045-a495e7fb47dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjdXRlJTIwbmV3Ym9ybiUyMGJhYnklMjBzbGVlcGluZyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MTY4OTgyNHww&ixlib=rb-4.1.0&q=85',
    category: 'newborn',
  },
  { 
    url: 'https://images.unsplash.com/photo-1739249411701-f76c8820b374?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBwaG90b2dyYXBoeSUyMHNlc3Npb24lMjBiZWF1dGlmdWwlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzcxNjkxMjk1fDA&ixlib=rb-4.1.0&q=85',
    category: 'maternity',
  },
  { 
    url: 'https://images.unsplash.com/photo-1761891950106-3276efeef9d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjdXRlJTIwbmV3Ym9ybiUyMGJhYnklMjBzbGVlcGluZyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MTY4OTgyNHww&ixlib=rb-4.1.0&q=85',
    category: 'newborn',
  },
  { 
    url: 'https://images.pexels.com/photos/6910423/pexels-photo-6910423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'family',
  },
  { 
    url: 'https://images.pexels.com/photos/13542476/pexels-photo-13542476.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'baptism',
  },
  { 
    url: 'https://images.unsplash.com/photo-1767082090422-2e5aeeba2afe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxwYXJlbnRzJTIwcGxheWluZyUyMHdpdGglMjBjaGlsZHJlbiUyMHBhcmslMjBoYXBweSUyMGxpZmVzdHlsZXxlbnwwfHx8fDE3NzE2OTE0ODJ8MA&ixlib=rb-4.1.0&q=85',
    category: 'family',
  },
  { 
    url: 'https://images.unsplash.com/photo-1761891954847-80d8ac6668ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxjdXRlJTIwbmV3Ym9ybiUyMGJhYnklMjBzbGVlcGluZyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MTY4OTgyNHww&ixlib=rb-4.1.0&q=85',
    category: 'newborn',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

export default function Gallery() {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = [
    { key: 'all', label: t.gallery.all },
    { key: 'family', label: t.gallery.family },
    { key: 'newborn', label: t.gallery.newborn },
    { key: 'maternity', label: t.gallery.maternity },
    { key: 'baptism', label: t.gallery.baptism },
  ];

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      if (response.data && response.data.length > 0) {
        // Use images from folders - backend returns full URLs
        setImages(response.data.map(img => ({
          url: img.url,
          category: img.category
        })));
      } else {
        // Use stock images as fallback
        setImages(stockImages);
      }
    } catch (error) {
      console.log('Using stock images');
      setImages(stockImages);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = activeFilter === 'all'
    ? images
    : images.filter(img => img.category === activeFilter);

  // Assign grid spans dynamically
  const getSpan = (index) => {
    if (index === 0) return 'col-span-2 row-span-2';
    if (index === 2) return 'col-span-1 row-span-2';
    if (index === 5) return 'col-span-2 row-span-1';
    return 'col-span-1 row-span-1';
  };

  return (
    <div data-testid="gallery-page" className="pt-24 bg-warm-50">
      {/* Header */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.gallery.subtitle}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-warm-900">
              {t.gallery.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-warm-100">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                data-testid={`filter-${filter.key}`}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 ${
                  activeFilter === filter.key
                    ? 'bg-accent text-white shadow-md'
                    : 'bg-white text-warm-600 hover:bg-warm-200 hover:text-warm-800'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-warm-500">A carregar...</p>
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] md:auto-rows-[220px]"
            >
              <AnimatePresence mode="popLayout">
                {filteredImages.map((img, index) => (
                  <motion.div
                    key={`${img.url}-${index}`}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                    className={`relative overflow-hidden rounded-xl cursor-pointer group ${getSpan(index)}`}
                    onClick={() => setSelectedImage(img)}
                    data-testid={`gallery-image-${index}`}
                  >
                    <img
                      src={img.url}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-warm-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="text-white text-sm font-medium bg-accent/80 px-3 py-1 rounded-full">
                        {t.gallery[img.category] || img.category}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
          
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-warm-900/95 backdrop-blur-lg p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors bg-white/10 rounded-full"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.url}
              alt="Selected"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
